/** Crown Institute — single dynamic page that handles every academy detail route.
 *  Routes: /academy/:slug — slug maps to either a CURRICULUM_SEED semester id OR
 *  a "special program" entry (entrepreneur-academy, municipal-certification, etc).
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import {
  ArrowRight, BookOpen, ClipboardCheck, FileText, Layers,
  Award, Users2, GraduationCap, Briefcase, Building2,
  Crown, Sparkles, Cpu,
} from "lucide-react";

// Slug → Semester order map (data lives in /api/academy/curriculum-public)
const SEMESTER_SLUG_TO_ORDER = {
  "civic-duty-foundation":           1,
  "american-history-foundation":     2,
  "constitution-and-rights":         3,
  "public-budget-and-taxation":      4,
  "financial-literacy":              5,
  "entrepreneurship":                6,
  "leadership-and-management":       7,
  "community-leadership-internship": 8,
};

// Special programs that aren't 1-of-the-8-semesters but live under /academy/*
const SPECIAL_PROGRAMS = {
  "course-catalog": {
    title: "Course Catalog",
    overview: "The complete Crown Institute course catalog — 8 academic semesters, the 4 foundational frameworks, and 5 university-grade certifications.",
    cta: { primary: { label: "View 8 Semesters", to: "/curriculum" }, secondary: { label: "Read the Frameworks", to: "/frameworks" } },
    Icon: BookOpen,
    redirect_to: "/curriculum",
  },
  "entrepreneur-academy": {
    title: "Entrepreneur Academy",
    overview: "Crown Institute's flagship business-formation track. $499 initiation + $99 per semester. Capstone: Business Financial Readiness Plan and a real LLC + EIN + business banking package.",
    bullets: [
      "Business formation, LLC setup, EIN registration, banking, business credit",
      "Bookkeeping, cash flow, taxes, financial statements, business planning",
      "Capstone: Business Financial Readiness Plan presented to Crown faculty panel",
      "Earns the CICE — Crown Institute Certified Entrepreneur credential",
    ],
    cta: { primary: { label: "Enroll in Entrepreneur Track", to: "/crown-institute-academy#enroll-section" }, secondary: { label: "View CICE Cert", to: "/certifications" } },
    Icon: Sparkles,
  },
  "municipal-certification": {
    title: "Municipal Certification (CICMP)",
    overview: "Professional credential for government employees, elected officials, and civic professionals. Satisfies Florida ethics training (§112.3142 F.S.) and cybersecurity awareness requirements.",
    bullets: [
      "Deep-dive of Semesters 1–4 (Civic Duty · History · Constitution · Public Budget)",
      "AuditIQ platform operations + Florida compliance modules",
      "Pass Municipal Professional Assessment at 9.2+",
      "Submit one real municipal case study applying all 4 frameworks",
    ],
    cta: { primary: { label: "Begin CICMP Path", to: "/certifications" }, secondary: { label: "Talk to Cities & Counties", to: "/contact" } },
    Icon: Building2,
  },
  "ai-command-specialist": {
    title: "AI Command Specialist (CICAI)",
    overview: "Advanced credential for professionals demonstrating disciplined, ethical, and operationally effective AI literacy in professional and business contexts.",
    bullets: [
      "AI literacy modules integrated across all 8 semesters",
      "AI Command Specialist Assessment at 9.2+",
      "Submit AI Integration Portfolio: 5 documented business applications + CHAT analysis of each",
      "AI output evaluation discipline + Five Pillars of AI misuse module",
    ],
    cta: { primary: { label: "Pursue CICAI", to: "/certifications" }, secondary: { label: "View All Frameworks", to: "/frameworks" } },
    Icon: Cpu,
  },
  "continuing-education": { redirect_to: "/continuing-education" },
};

const FrameworkPills = ({ items }) => (
  <div className="flex flex-wrap gap-2 mt-3">
    {(items || []).map(f => (
      <span key={f} className="text-xs uppercase tracking-wider px-2.5 py-1 rounded border border-gold-dim/40 text-gold">{f}</span>
    ))}
  </div>
);

const AcademyDetail = () => {
  const { slug } = useParams();
  const [section, setSection] = useState(null);
  const [special, setSpecial] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const order = SEMESTER_SLUG_TO_ORDER[slug];
    const sp = SPECIAL_PROGRAMS[slug];

    // Hard redirects (course-catalog → /curriculum, continuing-education → /continuing-education)
    if (sp?.redirect_to && !sp.title) {
      window.location.replace(sp.redirect_to);
      return;
    }

    if (order) {
      api.get("/academy/curriculum-public").then(r => {
        const found = (r.data.sections || []).find(s => s.order === order);
        setSection(found || null);
        setLoading(false);
      });
    } else if (sp) {
      setSpecial(sp);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [slug]);

  if (loading) return <Layout><section className="container-x section-y text-cream-dim">Loading…</section></Layout>;

  // ---- Special program render ----
  if (special) {
    const I = special.Icon || Crown;
    return (
      <Layout>
        <SEOHead title={`${special.title} | Crown Institute Academy`} path={`/academy/${slug}`} />
        <section className="container-x section-y">
          <div className="text-[11px] uppercase tracking-[0.28em] text-gold flex items-center gap-2">
            <I size={12} /> Crown Institute Academy
          </div>
          <h1 className="font-display mt-4">{special.title}</h1>
          <p className="text-cream-dim mt-4 max-w-2xl">{special.overview}</p>
        </section>
        {special.bullets && (
          <section className="bg-navy-deep border-y border-gold-dim/20 section-y">
            <div className="container-x">
              <h2 className="font-display">Program highlights</h2>
              <ul className="mt-6 space-y-3 max-w-3xl">
                {special.bullets.map((b, i) => (
                  <li key={i} className="card-premium glass flex items-start gap-3">
                    <span className="text-gold text-xl leading-none mt-1">•</span>
                    <span className="text-cream">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}
        <section className="container-x pb-16">
          <div className="card-premium glass flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-display">Take the next step</h3>
              <p className="text-cream-dim text-sm mt-1">Enroll, talk to admissions, or read the credential requirements.</p>
            </div>
            <div className="flex gap-3 btn-stack">
              <Link to={special.cta.primary.to} className="contents"><Button className="btn-gold h-12">{special.cta.primary.label} <ArrowRight size={14} className="ml-2" /></Button></Link>
              <Link to={special.cta.secondary.to} className="contents"><Button className="btn-ghost-gold h-12">{special.cta.secondary.label}</Button></Link>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  // ---- Semester render ----
  if (!section) {
    return (
      <Layout>
        <section className="container-x section-y">
          <h1 className="font-display">Course preview loading.</h1>
          <p className="text-cream-dim mt-4 max-w-xl">
            Full student access is available after enrollment. Visit the Course Catalog
            to browse all 8 semesters, or enroll today.
          </p>
          <div className="mt-8 flex gap-3 btn-stack">
            <Link to="/curriculum" className="contents"><Button className="btn-gold h-12">View Course Catalog</Button></Link>
            <Link to="/crown-institute-academy#enroll-section" className="contents"><Button className="btn-ghost-gold h-12">Enroll Now</Button></Link>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead title={`Semester ${section.order} · ${section.title} | Crown Institute Academy`} path={`/academy/${slug}`} />
      <section className="container-x section-y">
        <div className="text-[11px] uppercase tracking-[0.28em] text-gold flex items-center gap-2">
          <Crown size={12} /> Semester {section.order} of 8
        </div>
        <h1 className="font-display mt-4">{section.title}</h1>
        <FrameworkPills items={section.frameworks} />
        <p className="text-cream-dim mt-5 max-w-2xl">
          Pass standard: <span className="text-gold font-semibold">9.2 / 10</span> · Includes Written Exam,
          Scenario Exam, and Real-World Application.
        </p>
      </section>

      <section className="bg-navy-deep border-y border-gold-dim/20 section-y">
        <div className="container-x grid lg:grid-cols-2 gap-6">
          <div className="card-premium glass">
            <div className="text-[10px] uppercase tracking-[0.22em] text-gold flex items-center gap-2 mb-3">
              <BookOpen size={12} /> Lessons
            </div>
            <ul className="space-y-2">
              {(section.lessons || []).map((l, i) => (
                <li key={i} className="text-cream flex items-start gap-3">
                  <span className="font-display text-gold w-6 shrink-0">{i + 1}</span>
                  <span>{l}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-5">
            <div className="card-premium glass">
              <div className="text-[10px] uppercase tracking-[0.22em] text-electric flex items-center gap-2 mb-3">
                <FileText size={12} /> Assignment
              </div>
              <p className="text-cream leading-relaxed">{section.assignment}</p>
            </div>
            <div className="card-premium glass">
              <div className="text-[10px] uppercase tracking-[0.22em] text-electric flex items-center gap-2 mb-3">
                <ClipboardCheck size={12} /> Exam Components
              </div>
              <ul className="text-cream space-y-1.5">
                {(section.exams || []).map((e, i) => <li key={i}>· {e}</li>)}
              </ul>
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold mt-4 mb-2">Frameworks integrated</div>
              <FrameworkPills items={section.frameworks} />
            </div>
          </div>
        </div>
      </section>

      <section className="container-x pb-16">
        <div className="card-premium glass flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-display">Continue the program</h3>
            <p className="text-cream-dim text-sm mt-1">
              Every semester counts toward CICS — Crown Institute Certified Student.
            </p>
          </div>
          <div className="flex gap-3 btn-stack">
            <Link to="/crown-institute-academy#enroll-section" className="contents"><Button className="btn-gold h-12">Enroll Now <ArrowRight size={14} className="ml-2" /></Button></Link>
            <Link to="/student-login" className="contents"><Button className="btn-ghost-gold h-12">Student Login</Button></Link>
            <Link to="/curriculum" className="contents"><Button className="btn-ghost-gold h-12">Back to Catalog</Button></Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AcademyDetail;
